import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { packPer } from '../seed/wms-fg.seed'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  fail,
  handle,
  idOf,
  nextDocNo,
  pad,
  parse,
  requireRole,
  roleCodesOf,
  statusCodeOf,
  txLog,
  workflowRunner
} from './c07.shared'
import {
  type ClaimCategory,
  checkClaimComplete,
  claimLegacyStoreTasksOf,
  claimLockTarget,
  claimPutawayTasksOf
} from './claimPutaway'
import { containerFor, recommendPallet } from './fgInbound.handler'
import { notify, wmsRoute } from './notification.shared'
import { systemTransition } from './opname.shared'
import { registerReadHook } from './readHooks'
import { REPACK_ORIGIN } from './repackGuard'
import { clmAutoApprove } from './slaSetting'

/**
 * Endpoint custom Customer Claim FG — `/api/v1/wms/claim-fg/*` (WF-CLM-01 v7.50, entitas `claim`; laporan 23 §2.3,
 * keputusan user §9 / §9.1):
 * - CLM-01: `issue-sort` (Terima Barang & Terbitkan Tugas: claim + satu tugas sortir `SRT-`).
 * - CLM-03 (operator): `sort-scan`, `sort-decide` (Bagus, atau Buka → Good / Repack / Not Good), `sort-remove`,
 *   `sort-finish`; tugas simpan lama: `store-gate`, `store-save` (hanya claim sebelum v7.50 dan pengalihan Repack DSP).
 * - CLM-02: `approve` / `resort` (Supervisor), `process-preview` + `process` (Admin: label pack + tugas putaway FG per
 *   SKU), `labels` (Lihat Label), `label-history` (drawer Riwayat), `rule` (hitung mundur), `sla-sweep`.
 * Auto-approve: `autoApproveSweep()` (Master SLA `CLM_AUTO_APPROVE`: aktif, durasi, cakupan Not Good) dijalankan setiap
 * `claim` dibaca (kait baca) — produksi butuh penjadwal (D70). Claim Selesai otomatis lewat `claimPutaway.ts`.
 * Dihapus (keputusan user Q-12b, C-02, C-06): rencana kedatangan, import Excel, Retur Fisik / Tanpa Retur, pendataan,
 * pencocokan, Izin Bongkar, Tolak Claim, tugas bongkar terpisah, jalur Kadiv, order pengganti.
 */

const flow = workflowRunner('WF-CLM-01', 'claim')
const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
const iso = () => new Date().toISOString()
const minutesSince = (value: unknown, now = Date.now()) =>
  value ? Math.max(0, Math.floor((now - new Date(String(value)).getTime()) / 60000)) : 0
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
const isOperatorFg = (userId: unknown) => roleCodesOf(idOf(userId)).includes('operator_wh_fg')

export const claimOf = (id: unknown) => {
  const row = findRow('claim', String(id ?? ''))
  if (!row) fail('Claim tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}
export const itemsOf = (claim: LocalRow) =>
  readTable('claim_item')
    .filter(item => idOf(item.claim_id) === claim.id)
    .sort(
      (a, b) => String(a.date_created).localeCompare(String(b.date_created)) || String(a.id).localeCompare(String(b.id))
    )
const labelOf = (id: unknown) => findRow('fg_label', idOf(id)) as LocalRow
const skuOfLabel = (label: LocalRow) => findRow('mt_product', idOf(label.sku_id)) as LocalRow
const sortTaskOfClaim = (claim: LocalRow) =>
  readTable('claim_sort_task')
    .filter(task => idOf(task.claim_id) === claim.id)
    .sort((a, b) => String(b.date_created).localeCompare(String(a.date_created)))[0]
const reasonOf = (id: unknown, category: string) => {
  const row = findRow('mt_reject_type', idOf(id))
  const categories = (row?.categories ?? []) as string[]
  if (!row || !row.is_active || !categories.includes(category)) fail('Alasan tidak dikenal untuk kategori ini.')
  return row
}

/** Pack per satuan label (Karton / Bal / Slop) — 1 pack = 1 keping pita cukai. */
const packOfLabel = (label: LocalRow) => {
  const per = packPer(String(skuOfLabel(label).code))
  return label.level === 'Karton' ? per.karton : label.level === 'Bal' ? per.bal : per.slop
}
/** Asal kiriman label: order, Surat Jalan, customer, tanggal kirim (customer dan SJ claim dibaca dari sini — K-11). */
const shipmentOf = (label: LocalRow) => {
  const plan = findRow('delivery_plan', idOf(label.shipped_plan_id))
  return {
    plan_no: String(plan?.plan_no ?? ''),
    sj_no: String(plan?.sj_no ?? ''),
    customer_id: idOf(plan?.customer_id),
    shipped_at: String(plan?.shipped_at ?? '')
  }
}
const customerName = (id: unknown) => String(findRow('mt_companies', idOf(id))?.name ?? '')
const matchesLabel = (label: LocalRow, code: string) => {
  const scanned = up(code)
  const no = up(label.label_no)
  return Boolean(scanned) && (scanned === no || no.endsWith(`:${scanned}`))
}
const mostFrequent = (values: string[]) => {
  const count = new Map<string, number>()
  values.filter(Boolean).forEach(value => count.set(value, (count.get(value) ?? 0) + 1))
  return [...count.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? ''
}

export function claimTotals(claim: LocalRow) {
  const items = itemsOf(claim)
  const sum = (field: string) => items.reduce((acc, item) => acc + Number(item[field] ?? 0), 0)
  return { good: sum('qty_good'), repack: sum('qty_repack'), ng: sum('qty_ng') }
}

/* ============================================================ CLM-01 — Terima Barang & Terbitkan Tugas */

type OperatorPart = { operator_id: string; part_name: string | null; done_at: string | null }
const partsOf = (task: LocalRow) => (task.operator_parts ?? []) as OperatorPart[]

export function issueSort(body: { operators?: { operator_id?: string; part_name?: string }[] }) {
  requireRole('Terima barang claim hanya untuk Admin WH FG.', 'admin_wh_fg')
  const operators = (body.operators ?? []).filter(row => row.operator_id)
  if (!operators.length) fail('Pilih minimal satu petugas.')
  if (new Set(operators.map(row => row.operator_id)).size !== operators.length)
    fail('Petugas yang sama dipilih dua kali.')
  operators.forEach(row => {
    if (!isOperatorFg(row.operator_id)) fail('Petugas harus Operator WH FG.')
  })
  if (operators.length > 1 && operators.some(row => String(row.part_name ?? '').trim().length < 2))
    fail('Karena petugasnya lebih dari satu, tiap petugas harus diberi nama bagian yang terbaca di lantai.')
  const claimId = newId()
  const { statusId, submissionId } = flow.start(claimId)
  const clmNo = nextDocNo('CLM', 'claim', 'clm_no')
  insertRow('claim', {
    ...auditFieldsForCreate(),
    id: claimId,
    clm_no: clmNo,
    customer_id: null,
    sj_list: null,
    status_id: statusId,
    submission_id: submissionId,
    lot_no: `LOT-${clmNo}`,
    sort_locked_at: null,
    d2_decided_by_id: null,
    d2_decided_at: null,
    d2_auto: false,
    return_reason: null,
    resort_count: 0,
    processed_at: null,
    processed_by_id: null,
    is_completed: false,
    completed_at: null
  })
  const srtNo = nextDocNo('SRT', 'claim_sort_task', 'bkr_no')
  const task = insertRow('claim_sort_task', {
    ...auditFieldsForCreate(),
    id: newId(),
    bkr_no: srtNo,
    claim_id: claimId,
    operator_id: operators[0].operator_id,
    operator_parts: operators.map(row => ({
      operator_id: String(row.operator_id),
      part_name: operators.length > 1 ? String(row.part_name ?? '').trim() : null,
      done_at: null
    })),
    task_status: 'ASSIGNED',
    locked_at: null
  })
  const names = operators.map(row => userName(row.operator_id)).join(', ')
  txLog('FG Claim', 'CLM-01', 'clm-terima-tugas', clmNo, `Barang claim diterima — tugas sortir ${srtNo} ke ${names}`)
  operators.forEach(row =>
    notify({
      to: { userIds: [row.operator_id] },
      type: 'CLAIM_SORT_ASSIGNED',
      title: `Tugas sortir claim ${clmNo}`,
      body: `Scan dan putuskan tiap unit barang claim${operators.length > 1 ? ` (bagian ${String(row.part_name ?? '').trim()})` : ''} — buka Claim — Operator.`,
      link: wmsRoute('customer-claim/claim-operator', { sort: String(task.id) }),
      ref: { type: 'claim', id: claimId }
    })
  )
  return { claim_id: claimId, clm_no: clmNo, task_id: task.id, srt_no: srtNo, operators: names }
}

/* ============================================================ CLM-03 — sortir (operator) */

function sortContext(taskId: unknown) {
  const task = findRow('claim_sort_task', String(taskId ?? ''))
  if (!task) fail('Tugas sortir tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const mine = partsOf(task).find(part => part.operator_id === currentUserId())
  if (!mine) fail('Aksi hanya untuk petugas yang ditugaskan.', 'ERR_UNAUTHORIZED_ROLE', 403)
  const claim = claimOf(task.claim_id)
  if (statusCodeOf(claim) !== 'CLM_SORTING' || task.task_status !== 'ASSIGNED')
    fail('Sortir claim ini sudah dikirim ke Supervisor.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (mine.done_at) fail('Bagian Anda sudah dikirim — tunggu petugas lain.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  return { task, claim, mine }
}

export function sortScan(body: { task_id?: string; code?: string }) {
  const { task, claim } = sortContext(body.task_id)
  const code = String(body.code ?? '').trim()
  if (!code) fail('Kode kosong.')
  const label = readTable('fg_label').find(row => matchesLabel(row, code))
  if (!label) fail(`Yang discan (${code}) bukan label barang kiriman JAL.`, 'ERR_UNKNOWN_LABEL', 422)
  const already = itemsOf(claim).find(item => idOf(item.label_id) === label.id)
  if (already)
    fail(
      `Label ini sudah tercatat di claim ${String(claim.clm_no)} (discan ${userName(already.scanned_by_id) || 'petugas'}).`,
      'ERR_DUPLICATE_SCAN',
      409
    )
  if (label.label_status !== 'SHIPPED')
    fail(
      `Yang discan belum pernah dikirim ke customer atau sudah diklaim (status ${String(label.label_status)}).`,
      'ERR_LABEL_NOT_SHIPPED',
      422
    )
  if (!['Karton', 'Bal', 'Slop'].includes(String(label.level)))
    fail('Hanya karton, bal, atau slop berlabel yang bisa discan.', 'ERR_LABEL_LEVEL', 422)
  const shipment = shipmentOf(label)
  if (!shipment.sj_no) fail('Yang discan tidak memiliki Surat Jalan.', 'ERR_LABEL_NOT_SHIPPED', 422)
  const item = insertRow('claim_item', {
    ...auditFieldsForCreate(),
    id: newId(),
    claim_id: claim.id,
    label_id: label.id,
    equivalent_pack: packOfLabel(label),
    sort_task_id: task.id,
    sort_result: null,
    qty_good: null,
    qty_repack: null,
    qty_ng: null,
    repack_reason_id: null,
    reject_reason_id: null,
    sj_no: shipment.sj_no,
    shipped_at: shipment.shipped_at || null,
    order_no: shipment.plan_no || null,
    customer_id: shipment.customer_id || null,
    scanned_by_id: currentUserId(),
    sorted_at: null,
    sorted_by_id: null
  })
  updateRow('fg_label', String(label.id), { label_status: 'CLAIM_RECEIVED', ...auditFieldsForUpdate() })
  return {
    item_id: item.id,
    label_no: label.label_no,
    sku_code: skuOfLabel(label).code,
    unit: label.level,
    packs: packOfLabel(label),
    sj_no: shipment.sj_no,
    shipped_at: shipment.shipped_at,
    customer: customerName(shipment.customer_id),
    total: itemsOf(claim).length
  }
}

const sortItemContext = (itemId: unknown) => {
  const item = findRow('claim_item', String(itemId ?? ''))
  if (!item) fail('Unit tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return { item, ...sortContext(item.sort_task_id) }
}

export type SortDecision = 'GOOD' | 'OPEN'

/** Bagus = unit utuh Good di tingkatnya; Buka = isinya dipilah per pack jadi Good / Repack / Not Good (alasan per unit). */
export function sortDecide(body: {
  item_id?: string
  decision?: SortDecision
  good?: number
  repack?: number
  ng?: number
  repack_reason_id?: string
  ng_reason_id?: string
}) {
  const { item, claim } = sortItemContext(body.item_id)
  const total = Number(item.equivalent_pack ?? 0)
  let patch: Record<string, unknown>
  if (body.decision === 'GOOD') {
    patch = {
      sort_result: 'GOOD_INTACT',
      qty_good: total,
      qty_repack: 0,
      qty_ng: 0,
      repack_reason_id: null,
      reject_reason_id: null
    }
  } else if (body.decision === 'OPEN') {
    const good = Number(body.good ?? 0)
    const repack = Number(body.repack ?? 0)
    const ng = Number(body.ng ?? 0)
    if (![good, repack, ng].every(value => Number.isInteger(value) && value >= 0) || good + repack + ng !== total)
      fail(`Isian belum pas — Good + Repack + Not Good harus ${total} pack.`)
    if (repack > 0) reasonOf(body.repack_reason_id, 'FG_REPACK')
    if (ng > 0) reasonOf(body.ng_reason_id, 'FG_NG')
    patch = {
      sort_result: 'OPENED',
      qty_good: good,
      qty_repack: repack,
      qty_ng: ng,
      repack_reason_id: repack > 0 ? body.repack_reason_id : null,
      reject_reason_id: ng > 0 ? body.ng_reason_id : null
    }
  } else return fail('Pilih Bagus atau Buka.')
  updateRow('claim_item', String(item.id), {
    ...patch,
    sorted_at: iso(),
    sorted_by_id: currentUserId(),
    ...auditFieldsForUpdate()
  })
  const left = itemsOf(claim).filter(row => !row.sorted_at).length
  return { item_id: item.id, sort_result: patch.sort_result, remaining: left }
}

export function sortRemove(body: { item_id?: string }) {
  const { item } = sortItemContext(body.item_id)
  deleteRow('claim_item', String(item.id))
  updateRow('fg_label', idOf(item.label_id), { label_status: 'SHIPPED', ...auditFieldsForUpdate() })
  return { removed: true }
}

export function sortFinish(body: { task_id?: string }) {
  const { task, claim, mine } = sortContext(body.task_id)
  const items = itemsOf(claim)
  const own = items.filter(item => idOf(item.scanned_by_id) === currentUserId())
  if (!items.length) fail('Belum ada barang yang discan.')
  if (own.some(item => !item.sorted_at))
    fail(`Masih ${own.filter(item => !item.sorted_at).length} unit Anda yang belum diputuskan.`)
  const parts = partsOf(task).map(part => (part === mine ? { ...part, done_at: iso() } : part))
  updateRow('claim_sort_task', String(task.id), { operator_parts: parts, ...auditFieldsForUpdate() })
  const waiting = parts.filter(part => !part.done_at).length
  const totals = claimTotals(claim)
  if (waiting) return { submitted: false, waiting, ...totals }
  if (items.some(item => !item.sorted_at)) fail('Masih ada unit yang belum diputuskan petugas lain.')
  updateRow('claim_sort_task', String(task.id), { task_status: 'DONE', locked_at: iso(), ...auditFieldsForUpdate() })
  const labels = items.map(item => labelOf(item.label_id))
  flow.run(claim, 'ACT_CLM_FINISH_SORT', ['ACT_CLM_OP_FG'], {
    customer_id: mostFrequent(items.map(item => idOf(item.customer_id))) || null,
    sj_list: [...new Set(items.map(item => String(item.sj_no ?? '')).filter(Boolean))].join(', ') || null,
    sort_locked_at: iso()
  })
  txLog(
    'FG Claim',
    'CLM-03',
    'clm-sortir-selesai',
    String(claim.clm_no),
    `${labels.length} unit · Good ${totals.good} · Repack ${totals.repack} · Not Good ${totals.ng} pack — diajukan ke Supervisor`
  )
  return { submitted: true, waiting: 0, ...totals }
}

/* ============================================================ CLM-02 — keputusan Supervisor & auto-approve */

const requireSupervisor = () => requireRole('Keputusan hasil sortir hanya untuk Supervisor WH FG.', 'supervisor_wh_fg')
const pendingClaim = (id: unknown) => {
  const claim = claimOf(id)
  if (statusCodeOf(claim) !== 'CLM_PENDING_SORT_APP')
    fail('Claim ini tidak menunggu approval hasil sortir.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  return claim
}
const totalsText = (claim: LocalRow) => {
  const totals = claimTotals(claim)
  return `Good ${totals.good} · Repack ${totals.repack} · Not Good ${totals.ng} pack`
}

export function approveSort(body: { claim_id?: string }) {
  requireSupervisor()
  const claim = pendingClaim(body.claim_id)
  flow.run(claim, 'ACT_CLM_SPV_APPROVE', ['ACT_CLM_SPV_FG'], {
    d2_decided_by_id: currentUserId(),
    d2_decided_at: iso(),
    d2_auto: false
  })
  txLog(
    'FG Claim',
    'CLM-02',
    'clm-approve-sortir',
    String(claim.clm_no),
    `${totalsText(claim)} disetujui ${userName(currentUserId())}`
  )
  return { clm_no: claim.clm_no, ...claimTotals(claim) }
}

/** Minta Sortir Ulang: catatan wajib, keputusan tiap unit TETAP, jumlahnya tidak dibatasi; jam auto-approve mulai lagi saat Selesai berikutnya. */
export function resortClaim(body: { claim_id?: string; reason?: string }) {
  requireSupervisor()
  const claim = pendingClaim(body.claim_id)
  const reason = String(body.reason ?? '').trim()
  if (reason.length < 3) fail('Catatan untuk petugas wajib diisi (minimal 3 huruf).')
  const task = sortTaskOfClaim(claim)
  flow.run(
    claim,
    'ACT_CLM_RESORT',
    ['ACT_CLM_SPV_FG'],
    { return_reason: reason, resort_count: Number(claim.resort_count ?? 0) + 1, sort_locked_at: null },
    reason
  )
  if (task)
    updateRow('claim_sort_task', String(task.id), {
      task_status: 'ASSIGNED',
      locked_at: null,
      operator_parts: partsOf(task).map(part => ({ ...part, done_at: null })),
      ...auditFieldsForUpdate()
    })
  txLog('FG Claim', 'CLM-02', 'clm-sortir-ulang', String(claim.clm_no), `Diminta sortir ulang — ${reason}`)
  if (task)
    partsOf(task).forEach(part =>
      notify({
        to: { userIds: [part.operator_id] },
        type: 'CLAIM_SORT_ASSIGNED',
        title: `Sortir claim ${String(claim.clm_no)} diminta diulang`,
        body: `${reason} — keputusan sebelumnya tetap tersimpan; perbaiki yang perlu lalu Selesai lagi.`,
        link: wmsRoute('customer-claim/claim-operator', { sort: String(task.id) }),
        ref: { type: 'claim', id: String(claim.id) },
        severity: 'warning'
      })
    )
  return { clm_no: claim.clm_no }
}

/** Aturan Master SLA `CLM_AUTO_APPROVE` untuk hitung mundur di CLM-01 / CLM-02 / CLM-03. */
export const autoApproveRule = () => {
  const rule = clmAutoApprove()
  return {
    enabled: rule.enabled,
    minutes: rule.minutes,
    value: rule.value,
    unit: rule.unit,
    include_not_good: rule.includeNotGood
  }
}

/**
 * Setujui otomatis hasil sortir yang melewati batas Master SLA sejak Selesai terakhir (`sort_locked_at`). Aturan mati →
 * tidak pernah berjalan. Cakupan "Sertakan barang Not Good" = tidak → claim yang memuat Not Good tetap menunggu Supervisor.
 */
export function autoApproveSweep(now = Date.now()) {
  const rule = clmAutoApprove()
  const approved: string[] = []
  if (!rule.enabled) return { approved }
  readTable('claim')
    .filter(claim => statusCodeOf(claim) === 'CLM_PENDING_SORT_APP' && claim.sort_locked_at)
    .filter(claim => minutesSince(claim.sort_locked_at, now) >= rule.minutes)
    .filter(claim => rule.includeNotGood || claimTotals(claim).ng === 0)
    .forEach(claim => {
      systemTransition(
        'WF-CLM-01',
        claim,
        'ACT_CLM_AUTO_APPROVE',
        `Disetujui otomatis oleh sistem — belum diputuskan Supervisor dalam ${rule.value} ${rule.unit === 'HOUR' ? 'jam' : 'menit'}`
      )
      updateRow('claim', String(claim.id), {
        d2_decided_by_id: null,
        d2_decided_at: new Date(now).toISOString(),
        d2_auto: true
      })
      txLog(
        'FG Claim',
        'CLM-02',
        'clm-auto-setuju',
        String(claim.clm_no),
        `${totalsText(claim)} disetujui otomatis setelah ${rule.value} ${rule.unit === 'HOUR' ? 'jam' : 'menit'} (Master SLA)`
      )
      approved.push(String(claim.clm_no))
    })
  return { approved }
}

/* ============================================================ CLM-02 — Proses Claim → Tugaskan Putaway */

type PutawayLine = {
  category: ClaimCategory
  sku_id: string
  unit_type: string
  qty: number
  label_id: string | null
  label_no: string | null
  batch_id: string | null
  ng_pack_id: string | null
  source_item_ids: string[]
  reason: string | null
}

const CATEGORY_LABEL: Record<ClaimCategory, string> = { GOOD: 'Good', REPACK: 'Repack', NG: 'Not Good' }
const batchNoOf = (id: unknown) => String(findRow('fg_labeler_batch', idOf(id))?.batch_no ?? '')

/** Nomor NG `NGP-YYMM-nnn` (pola seed C12) — satu label QR per baris Not Good. */
function nextNgNo(reserved: string[]) {
  const date = new Date()
  const prefix = `NGP-${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1, 2)}-`
  const taken = [...readTable('fg_ng_pack').map(row => String(row.ng_no ?? '')), ...reserved]
  const highest = taken
    .filter(no => no.startsWith(prefix))
    .reduce((max, no) => Math.max(max, Number(no.slice(prefix.length, prefix.length + 3)) || 0), 0)
  return `${prefix}${pad(highest + 1, 3)}`
}

/**
 * Baris putaway sebuah claim yang disetujui (pure — tidak menulis): unit Bagus utuh (label aslinya), pack Good dan Repack
 * per SKU + batch (satu label QR baru), Not Good satu label per baris unit (nomor NG). Not Good yang sudah tersimpan di
 * karantina (claim lama) tidak ditugaskan lagi.
 */
function planPutaway(claim: LocalRow): PutawayLine[] {
  const lines: PutawayLine[] = []
  const lot = String(claim.lot_no ?? `LOT-${String(claim.clm_no)}`)
  const items = itemsOf(claim).filter(item => item.sorted_at)
  items
    .filter(item => item.sort_result === 'GOOD_INTACT')
    .forEach(item => {
      const label = labelOf(item.label_id)
      lines.push({
        category: 'GOOD',
        sku_id: idOf(label.sku_id),
        unit_type: String(label.level),
        qty: 1,
        label_id: String(label.id),
        label_no: String(label.label_no),
        batch_id: idOf(label.batch_id) || null,
        ng_pack_id: null,
        source_item_ids: [String(item.id)],
        reason: null
      })
    })
  const opened = items.filter(item => item.sort_result !== 'GOOD_INTACT')
  const grouped = (field: 'qty_good' | 'qty_repack', category: 'GOOD' | 'REPACK') => {
    const map = new Map<string, PutawayLine>()
    opened
      .filter(item => Number(item[field] ?? 0) > 0)
      .forEach(item => {
        const label = labelOf(item.label_id)
        const key = `${idOf(label.sku_id)}|${idOf(label.batch_id)}`
        const row =
          map.get(key) ??
          ({
            category,
            sku_id: idOf(label.sku_id),
            unit_type: 'Pack',
            qty: 0,
            label_id: null,
            label_no: null,
            batch_id: idOf(label.batch_id) || null,
            ng_pack_id: null,
            source_item_ids: [],
            reason:
              category === 'REPACK' ? String(findRow('mt_reject_type', idOf(item.repack_reason_id))?.name ?? '') : null
          } as PutawayLine)
        row.qty += Number(item[field])
        row.source_item_ids.push(String(item.id))
        map.set(key, row)
      })
    return [...map.values()].map((row, index) => ({
      ...row,
      label_no: `PK-${lot.replace(/^LOT-/, '')}-${category === 'GOOD' ? 'G' : 'R'}${pad(index + 1, 2)}`
    }))
  }
  lines.push(...grouped('qty_good', 'GOOD'), ...grouped('qty_repack', 'REPACK'))
  const reserved: string[] = []
  opened
    .filter(item => Number(item.qty_ng ?? 0) > 0)
    .forEach(item => {
      const existing = readTable('fg_ng_pack').find(pack => idOf(pack.claim_item_id) === item.id)
      if (existing && existing.ng_status !== 'AWAITING_STORE') return
      const label = labelOf(item.label_id)
      const ngNo = existing ? String(existing.ng_no) : nextNgNo(reserved)
      reserved.push(ngNo)
      lines.push({
        category: 'NG',
        sku_id: idOf(label.sku_id),
        unit_type: 'Pack',
        qty: Number(item.qty_ng),
        label_id: null,
        label_no: ngNo,
        batch_id: idOf(label.batch_id) || null,
        ng_pack_id: existing ? String(existing.id) : null,
        source_item_ids: [String(item.id)],
        reason: String(findRow('mt_reject_type', idOf(item.reject_reason_id))?.name ?? '')
      })
    })
  return lines
}

const printableOf = (
  line: Pick<PutawayLine, 'label_no' | 'sku_id' | 'batch_id' | 'qty' | 'category'>,
  lot: string
) => ({
  code: String(line.label_no),
  name: String(findRow('mt_product', line.sku_id)?.name ?? ''),
  sku_code: String(findRow('mt_product', line.sku_id)?.code ?? ''),
  category: line.category,
  qty: line.qty,
  meta: `${batchNoOf(line.batch_id) || lot} · ${line.qty} pack · ${CATEGORY_LABEL[line.category]}`
})

const requireViewer = () =>
  requireRole('Hanya Admin / Supervisor WH FG.', 'admin_wh_fg', 'supervisor_wh_fg', 'kadiv_wh_fg')

/** Pratinjau modal Tugaskan Putaway + stiker yang akan dicetak (kode sama dengan yang terbit saat Cetak). */
export function processPreview(params: { claim_id?: string } | undefined) {
  requireViewer()
  const claim = claimOf(params?.claim_id)
  const lot = String(claim.lot_no ?? '')
  const lines = planPutaway(claim)
  const packs = lines.filter(line => line.unit_type === 'Pack')
  const sum = (category: ClaimCategory) =>
    lines
      .filter(line => line.category === category)
      .reduce((acc, line) => acc + (line.unit_type === 'Pack' ? line.qty : 0), 0)
  return {
    clm_no: claim.clm_no,
    intact_units: lines.filter(line => line.unit_type !== 'Pack').length,
    labels: packs.map(line => printableOf(line, lot)),
    totals: { ...claimTotals(claim), good_pack: sum('GOOD'), repack_pack: sum('REPACK'), ng_pack: sum('NG') },
    targets: {
      repack: lines.some(line => line.category === 'REPACK') ? String(claimLockTarget('REPACK').code) : null,
      ng: lines.some(line => line.category === 'NG') ? String(claimLockTarget('NG').code) : null
    },
    sku_count: new Set(lines.map(line => line.sku_id)).size
  }
}

/**
 * Proses Claim → Tugaskan Putaway (satu modal, satu isian: petugas). Label pack Good, Repack, dan Not Good terbit; satu
 * `fg_putaway_task` sumber CLAIM per SKU untuk satu petugas; Repack / Not Good dikunci ke satu pallet tujuan. Dipanggil
 * sebagai "print = commit": mengembalikan stiker yang harus dicetak.
 */
export function processClaim(body: { claim_id?: string; operator_id?: string }) {
  requireRole('Tugaskan Putaway hanya untuk Admin WH FG.', 'admin_wh_fg')
  const claim = claimOf(body.claim_id)
  if (statusCodeOf(claim) !== 'CLM_SORT_APPROVED')
    fail('Hasil sortir claim ini belum disetujui atau sudah diproses.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (!body.operator_id) fail('Pilih petugas putaway.')
  if (!isOperatorFg(body.operator_id)) fail('Petugas putaway harus Operator WH FG.')
  const lines = planPutaway(claim)
  if (!lines.length) fail('Tidak ada barang claim yang perlu ditugaskan.')
  const lot = String(claim.lot_no ?? `LOT-${String(claim.clm_no)}`)
  const repackTarget = lines.some(line => line.category === 'REPACK') ? claimLockTarget('REPACK') : null
  const ngTarget = lines.some(line => line.category === 'NG') ? claimLockTarget('NG') : null
  const now = iso()
  /* baris Not Good: satu fg_ng_pack per baris unit, menunggu disimpan di karantina */
  lines
    .filter(line => line.category === 'NG')
    .forEach(line => {
      if (line.ng_pack_id) return
      const pack = insertRow('fg_ng_pack', {
        ...auditFieldsForCreate(),
        id: newId(),
        ng_no: line.label_no,
        claim_id: claim.id,
        claim_item_id: line.source_item_ids[0],
        pallet_id: null,
        qty_pack: line.qty,
        reason_id: idOf(findRow('claim_item', line.source_item_ids[0])?.reject_reason_id) || null,
        destruction_id: null,
        ng_status: 'AWAITING_STORE',
        queued_at: null,
        current_area: null,
        destroyed_at: null,
        parent_pack_id: null,
        note: null
      })
      line.ng_pack_id = String(pack.id)
    })
  const reserved = new Map<string, number>()
  const bySku = new Map<string, PutawayLine[]>()
  lines.forEach(line => bySku.set(line.sku_id, [...(bySku.get(line.sku_id) ?? []), line]))
  const tasks: LocalRow[] = []
  bySku.forEach((rows, skuId) => {
    const task = insertRow('fg_putaway_task', {
      ...auditFieldsForCreate(),
      id: newId(),
      fpw_no: nextDocNo('FPW', 'fg_putaway_task', 'fpw_no'),
      batch_id: null,
      sku_id: skuId,
      source_type: 'CLAIM',
      claim_id: claim.id,
      operator_id: body.operator_id,
      task_status: 'ASSIGNED',
      source_ref: String(claim.clm_no),
      note: 'Claim'
    })
    rows.forEach(line => {
      const locked = line.category !== 'GOOD'
      let targetPallet: LocalRow | null = null
      let targetContainer: LocalRow | null = null
      if (line.category === 'REPACK') targetPallet = repackTarget
      else if (line.category === 'NG') targetPallet = ngTarget
      else if (line.unit_type === 'Pack' || line.unit_type === 'Slop')
        targetContainer = containerFor(line.unit_type) ?? null
      else targetPallet = recommendPallet(skuId, line.unit_type, reserved)
      if (line.label_id) updateRow('fg_label', line.label_id, { label_status: 'ASSIGNED', ...auditFieldsForUpdate() })
      insertRow('fg_putaway_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: task.id,
        unit_type: line.unit_type,
        target_pallet_id: targetPallet?.id ?? null,
        target_container_id: targetContainer?.id ?? null,
        qty: line.qty,
        override_reason: null,
        label_id: line.label_id,
        label_no: line.label_no,
        batch_id: line.batch_id,
        category: line.category,
        is_target_locked: locked,
        ng_pack_id: line.ng_pack_id,
        source_item_ids: line.source_item_ids,
        done_qty: 0,
        actual_pallet_id: null,
        actual_container_id: null,
        item_status: 'PENDING'
      })
    })
    tasks.push(task)
  })
  flow.run(findRow('claim', String(claim.id)) as LocalRow, 'ACT_CLM_PROCESS', ['ACT_CLM_ADMIN_FG'], {
    processed_at: now,
    processed_by_id: currentUserId()
  })
  const printed = lines.filter(line => line.unit_type === 'Pack')
  const summary = (['GOOD', 'REPACK', 'NG'] as ClaimCategory[])
    .map(category => `${CATEGORY_LABEL[category]} ${lines.filter(line => line.category === category).length}`)
    .join(', ')
  txLog(
    'FG Claim',
    'CLM-02',
    'clm-tugaskan-putaway',
    String(claim.clm_no),
    `${printed.length} label pack dicetak; ${tasks.length} tugas putaway (${tasks.map(task => task.fpw_no).join(', ')}) untuk ${userName(body.operator_id)} — ${summary}`
  )
  tasks.forEach(task =>
    notify({
      to: { userIds: [body.operator_id] },
      type: 'PUTAWAY_ASSIGNED',
      title: `Tugas putaway FG ${String(task.fpw_no)} (claim ${String(claim.clm_no)})`,
      body: `Simpan barang claim ${String(claim.clm_no)} — ${String(findRow('mt_product', idOf(task.sku_id))?.code ?? '')}; Repack dan Not Good terkunci ke satu tujuan — buka Putaway FG — Operator.`,
      link: wmsRoute('inbound-fg/putaway-fg-operator', { task: String(task.id) }),
      ref: { type: 'fg_putaway_task', id: String(task.id) }
    })
  )
  return {
    clm_no: claim.clm_no,
    tasks: tasks.map(task => ({ id: task.id, fpw_no: task.fpw_no })),
    labels: printed.map(line => printableOf(line, lot))
  }
}

/** Lihat Label: stiker pack claim yang sudah terbit (cetak ulang tanpa nomor baru). */
export function claimLabels(params: { claim_id?: string } | undefined) {
  requireViewer()
  const claim = claimOf(params?.claim_id)
  const lot = String(claim.lot_no ?? '')
  const taskIds = new Set(claimPutawayTasksOf(String(claim.id)).map(task => String(task.id)))
  return readTable('fg_putaway_task_item')
    .filter(item => taskIds.has(idOf(item.task_id)) && item.unit_type === 'Pack' && item.label_no)
    .map(item =>
      printableOf(
        {
          label_no: String(item.label_no),
          sku_id: idOf(findRow('fg_putaway_task', idOf(item.task_id))?.sku_id),
          batch_id: idOf(item.batch_id) || null,
          qty: Number(item.qty),
          category: item.category as ClaimCategory
        },
        lot
      )
    )
}

/* ============================================================ CLM-02 — Riwayat label (drawer) */

type HistoryEvent = { at: string; title: string; detail: string; tone: 'info' | 'ok' | 'warn' | 'bad' }

const locationCode = (item: LocalRow) =>
  String(
    findRow('pallet', idOf(item.actual_pallet_id))?.code ??
      findRow('container', idOf(item.actual_container_id))?.code ??
      ''
  )
const decisionText = (item: LocalRow) =>
  item.sort_result === 'GOOD_INTACT'
    ? 'Bagus — Good, disimpan utuh'
    : `Dibuka — Good ${Number(item.qty_good ?? 0)} · Repack ${Number(item.qty_repack ?? 0)} · Not Good ${Number(item.qty_ng ?? 0)} pack` +
      (item.repack_reason_id
        ? ` · alasan Repack: ${String(findRow('mt_reject_type', idOf(item.repack_reason_id))?.name ?? '')}`
        : '') +
      (item.reject_reason_id
        ? ` · alasan Not Good: ${String(findRow('mt_reject_type', idOf(item.reject_reason_id))?.name ?? '')}`
        : '')

/** Identitas label + log transaksi dari dibuat di Auto Labeler sampai tersimpan lagi (C-11, DERIVE). */
export function labelHistory(params: { claim_id?: string; item_id?: string } | undefined) {
  requireViewer()
  const claim = claimOf(params?.claim_id)
  const item = findRow('claim_item', String(params?.item_id ?? ''))
  if (!item || idOf(item.claim_id) !== claim.id) fail('Label tidak ada di claim ini.', 'ERR_NOT_FOUND', 404)
  const label = labelOf(item.label_id)
  const sku = skuOfLabel(label)
  const batch = findRow('fg_labeler_batch', idOf(label.batch_id))
  const shipment = shipmentOf(label)
  const events: HistoryEvent[] = []
  const add = (at: unknown, title: string, detail: string, tone: HistoryEvent['tone'] = 'info') =>
    at && events.push({ at: String(at), title, detail, tone })
  add(
    label.date_created ?? batch?.date_created,
    'Label dibuat di Auto Labeler',
    `${String(label.level)} ${String(sku.code)}${batch ? ` · batch ${String(batch.batch_no)}` : ''}`
  )
  const prodItem = readTable('fg_putaway_task_item').find(row => {
    const task = findRow('fg_putaway_task', idOf(row.task_id))
    return idOf(row.label_id) === label.id && row.item_status === 'DONE' && task?.source_type !== 'CLAIM'
  })
  if (prodItem)
    add(
      prodItem.date_updated ?? prodItem.date_created,
      'Disimpan di penyimpanan FG',
      `Putaway ${String(findRow('fg_putaway_task', idOf(prodItem.task_id))?.fpw_no ?? '')} · ${locationCode(prodItem)}`
    )
  if (shipment.shipped_at)
    add(
      shipment.shipped_at,
      `Dikirim lewat Surat Jalan ${shipment.sj_no}`,
      `${customerName(shipment.customer_id)}${shipment.plan_no ? ` · order ${shipment.plan_no}` : ''}`
    )
  add(claim.date_created, `Claim ${String(claim.clm_no)} dibuat`, userName(claim.user_created))
  const task = findRow('claim_sort_task', idOf(item.sort_task_id))
  if (task)
    add(
      task.date_created,
      'Barang diterima dan sortir dimulai',
      `Tugas ${String(task.bkr_no)} · ${partsOf(task)
        .map(part => userName(part.operator_id))
        .join(', ')}`
    )
  add(item.date_created, 'Discan petugas', userName(item.scanned_by_id ?? item.user_created))
  if (item.sorted_at)
    add(
      item.sorted_at,
      'Diputuskan petugas',
      `${decisionText(item)}${item.sorted_by_id ? ` · ${userName(item.sorted_by_id)}` : ''}`,
      Number(item.qty_ng ?? 0) > 0 ? 'bad' : 'ok'
    )
  const actionName = (row: LocalRow) => String(findRow('wml_action', idOf(row.action_id))?.name ?? '')
  readTable('wxl_submission_log')
    .filter(row => idOf(row.submission_id) === idOf(claim.submission_id))
    .forEach(row => {
      const name = actionName(row)
      if (name === 'ACT_CLM_FINISH_SORT')
        add(row.date_created, 'Hasil sortir diajukan ke Supervisor WH FG', userName(row.user_created))
      if (name === 'ACT_CLM_RESORT')
        add(
          row.date_created,
          `Diminta sortir ulang oleh ${userName(row.user_created)}`,
          String(row.reason ?? ''),
          'warn'
        )
      if (name === 'ACT_CLM_SPV_APPROVE') add(row.date_created, `Disetujui ${userName(row.user_created)}`, '', 'ok')
      if (name === 'ACT_CLM_AUTO_APPROVE')
        add(row.date_created, 'Disetujui otomatis oleh sistem', String(row.reason ?? ''), 'ok')
      if (name === 'ACT_CLM_AUTO_COMPLETE')
        add(row.date_created, 'Claim selesai otomatis', String(row.reason ?? ''), 'ok')
    })
  if (claim.processed_at)
    add(
      claim.processed_at,
      'Claim diproses — label pack dicetak dan tugas putaway terbit',
      `oleh ${userName(claim.processed_by_id)}`
    )
  const taskIds = new Set(claimPutawayTasksOf(String(claim.id)).map(row => String(row.id)))
  readTable('fg_putaway_task_item')
    .filter(
      row => taskIds.has(idOf(row.task_id)) && ((row.source_item_ids ?? []) as string[]).includes(String(item.id))
    )
    .forEach(row => {
      const putaway = findRow('fg_putaway_task', idOf(row.task_id)) as LocalRow
      const tag = row.category === 'REPACK' ? 'Repack' : row.category === 'NG' ? 'Not Good' : 'Retur'
      add(
        putaway.date_created,
        'Tugas penyimpanan diterbitkan',
        `Putaway ${String(putaway.fpw_no)} · ${row.unit_type === 'Pack' ? `${String(row.label_no)} · ${Number(row.qty)} pack ${tag}` : String(row.unit_type)} · ${userName(putaway.operator_id)}`
      )
      if (row.item_status === 'DONE')
        add(
          row.date_updated ?? putaway.date_updated,
          `Tersimpan di ${row.category === 'NG' ? 'karantina NG FG' : row.category === 'REPACK' ? 'Zone Retur Claim' : 'penyimpanan FG'} (tanda ${tag})`,
          `Putaway ${String(putaway.fpw_no)} · ${locationCode(row)}`,
          'ok'
        )
    })
  claimLegacyStoreTasksOf(String(claim.id))
    .filter(row => row.task_status === 'DONE')
    .forEach(row =>
      add(
        row.done_at,
        'Tersimpan lewat tugas simpan (claim lama)',
        `${String(row.spc_no)} · ${Number(row.stored_packs ?? 0)} pack ${String(row.category)} → ${String(findRow('pallet', idOf(row.target_pallet_id))?.code ?? '')}`,
        'ok'
      )
    )
  return {
    identity: {
      label_no: String(label.label_no),
      sku_code: String(sku.code),
      sku_name: String(sku.name),
      unit: String(label.level),
      sj_no: String(item.sj_no ?? shipment.sj_no),
      shipped_at: String(item.shipped_at ?? shipment.shipped_at ?? ''),
      customer: customerName(item.customer_id ?? shipment.customer_id),
      equivalent_pack: Number(item.equivalent_pack ?? 0),
      clm_no: String(claim.clm_no),
      batch_no: String(batch?.batch_no ?? '')
    },
    events: events.sort((a, b) => a.at.localeCompare(b.at))
  }
}

/* ============================================================ CLM-03 — tugas simpan lama (claim < v7.50 & pengalihan Repack) */

const storeTaskOf = (id: unknown) => {
  const task = findRow('claim_store_task', String(id ?? ''))
  if (!task) fail('Tugas simpan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (idOf(task.operator_id) !== currentUserId())
    fail('Hanya petugas yang ditugaskan bisa menyimpan barangnya.', 'ERR_UNAUTHORIZED_ROLE', 403)
  return task
}

export function storeGate(body: { task_id?: string; code?: string }) {
  const task = storeTaskOf(body.task_id)
  if (task.task_status !== 'ASSIGNED') fail('Tugas simpan ini sudah selesai.')
  const target = findRow('pallet', idOf(task.target_pallet_id)) as LocalRow
  if (up(body.code) !== up(target.code))
    fail(`Yang discan (${String(body.code ?? '')}) bukan pallet tujuan ${String(target.code)}.`)
  return { pallet_code: target.code, qty_packs: task.qty_packs }
}

/** Good / Repack claim lama → stok pack (`fg_pack_stock`) di pallet Zone Retur Claim, satu baris per unit claim. */
function postClaimPacks(claim: LocalRow, category: 'GOOD' | 'REPACK', pallet: LocalRow, stored: number) {
  const field = category === 'GOOD' ? 'qty_good' : 'qty_repack'
  const rows = itemsOf(claim).filter(item => Number(item[field] ?? 0) > 0)
  let remaining = stored
  let posted = 0
  rows.forEach((item, index) => {
    const label = labelOf(item.label_id)
    const planned = Number(item[field])
    const qty = index === rows.length - 1 ? remaining : Math.min(planned, remaining)
    remaining -= qty
    if (qty <= 0) return
    insertRow('fg_pack_stock', {
      ...auditFieldsForCreate(),
      id: newId(),
      sku_id: idOf(label.sku_id),
      batch_id: label.batch_id ?? null,
      container_id: null,
      pallet_id: pallet.id,
      claim_id: claim.id,
      claim_item_id: item.id,
      claim_category: category,
      lot_no: claim.lot_no ?? null,
      is_priority_lot: true,
      origin: category === 'REPACK' ? REPACK_ORIGIN : 'Retur',
      label_no: `${String(claim.lot_no ?? claim.clm_no)}:${category === 'GOOD' ? 'G' : 'R'}${pad(index + 1, 2)}`,
      qty_pack: qty,
      status: 'IN_STOCK'
    })
    posted += qty
  })
  return posted
}

/** Pengalihan Repack dari pemusnahan: pack berpindah dari karantina NG ke pallet Repack dan menjadi stok pack Repack. */
function postDivertedPacks(task: LocalRow, pallet: LocalRow, stored: number) {
  const packs = ((task.ng_pack_ids ?? []) as string[])
    .map(id => findRow('fg_ng_pack', id))
    .filter(Boolean) as LocalRow[]
  const claim = findRow('claim', idOf(task.claim_id))
  const item = findRow('claim_item', idOf(packs[0]?.claim_item_id))
  const label = item ? labelOf(item.label_id) : undefined
  insertRow('fg_pack_stock', {
    ...auditFieldsForCreate(),
    id: newId(),
    sku_id: label ? idOf(label.sku_id) : null,
    batch_id: label?.batch_id ?? null,
    container_id: null,
    pallet_id: pallet.id,
    claim_id: claim?.id ?? null,
    claim_item_id: item?.id ?? null,
    claim_category: 'REPACK',
    lot_no: claim?.lot_no ?? null,
    is_priority_lot: true,
    origin: REPACK_ORIGIN,
    label_no: `${String(packs[0]?.ng_no ?? task.spc_no)}-R`,
    qty_pack: stored,
    status: 'IN_STOCK'
  })
  packs.forEach(pack =>
    updateRow('fg_ng_pack', String(pack.id), {
      ng_status: 'REPACKED',
      current_area: String(pallet.code),
      ...auditFieldsForUpdate()
    })
  )
  return stored
}

export function storeSave(body: { task_id?: string; code?: string; qty?: number; reason?: string }) {
  const task = storeTaskOf(body.task_id)
  if (task.task_status !== 'ASSIGNED') fail('Tugas simpan ini sudah selesai.')
  const target = findRow('pallet', idOf(task.target_pallet_id)) as LocalRow
  if (up(body.code) !== up(target.code)) fail(`Scan pallet ${String(target.code)} lebih dulu.`)
  const qty = Number(body.qty)
  if (!Number.isInteger(qty) || qty < 0) fail('Isi jumlah packnya lebih dulu.')
  if (qty === 0) fail('Nol pack berarti tidak ada yang disimpan — batalkan tugasnya kalau memang begitu.')
  const planned = Number(task.qty_packs ?? 0)
  const reason = String(body.reason ?? '').trim()
  if (qty !== planned && reason.length < 3) fail('Jumlahnya berbeda dari hasil sortir. Tuliskan sebabnya dulu.')
  updateRow('claim_store_task', String(task.id), {
    task_status: 'DONE',
    stored_packs: qty,
    deviation_reason: qty !== planned ? reason : null,
    done_at: iso(),
    ...auditFieldsForUpdate()
  })
  const claim = claimOf(task.claim_id)
  let postedPacks = 0
  if (task.source === 'DIVERT') postedPacks = postDivertedPacks(task, target, qty)
  else if (task.category === 'GOOD' || task.category === 'REPACK')
    postedPacks = postClaimPacks(claim, task.category, target, qty)
  else if (task.category === 'NG')
    itemsOf(claim)
      .filter(
        item =>
          Number(item.qty_ng ?? 0) > 0 && !readTable('fg_ng_pack').some(pack => idOf(pack.claim_item_id) === item.id)
      )
      .forEach(item =>
        insertRow('fg_ng_pack', {
          ...auditFieldsForCreate(),
          id: newId(),
          ng_no: nextNgNo([]),
          claim_id: claim.id,
          claim_item_id: item.id,
          pallet_id: target.id,
          qty_pack: Number(item.qty_ng),
          reason_id: item.reject_reason_id ?? null,
          destruction_id: null,
          ng_status: 'IN_QUARANTINE',
          queued_at: iso(),
          current_area: 'ZN-01'
        })
      )
  txLog(
    'FG Claim',
    'CLM-03',
    'store-save',
    String(task.spc_no),
    `${qty} pack ${String(task.category)} → ${String(target.code)}${qty !== planned ? ` — ${reason}` : ''}`
  )
  const claimDone = checkClaimComplete(String(claim.id))
  return { stored: qty, pallet_code: target.code, claim_done: claimDone, stock_packs: postedPacks }
}

/* ============================================================ registrasi */

export function registerClaimFgHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/claim-fg'
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  const get = (path: string, run: (params: Record<string, string> | undefined) => unknown) =>
    mock
      .onGet(`${base}/${path}`)
      .reply(config => handle(() => run(config.params as Record<string, string> | undefined)))
  /* auto-approve berjalan setiap claim dibaca (daftar / detail / operator) — pola GAB-01 */
  registerReadHook(['claim', 'claim_sort_task'], () => autoApproveSweep())
  post('issue-sort', issueSort)
  post('sort-scan', sortScan)
  post('sort-decide', sortDecide)
  post('sort-remove', sortRemove)
  post('sort-finish', sortFinish)
  post('approve', approveSort)
  post('resort', resortClaim)
  post('sla-sweep', () => autoApproveSweep())
  get('rule', () => autoApproveRule())
  get('process-preview', processPreview)
  post('process', processClaim)
  get('labels', claimLabels)
  get('label-history', labelHistory)
  post('store-gate', storeGate)
  post('store-save', storeSave)
}
